import app from 'flarum/forum/app';
import Component from 'flarum/common/Component';
import Avatar from 'flarum/common/components/Avatar';
import Link from 'flarum/common/components/Link';
import Tooltip from 'flarum/common/components/Tooltip';
import username from 'flarum/common/helpers/username';
import formatNumber from 'flarum/common/utils/formatNumber';
import extractText from 'flarum/common/utils/extractText';
const PRE = 'forumaker-statser.forum.widget.';
function parseActivityMap(raw) {
  if (typeof raw !== 'string' || !raw) return {};
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch (e) {
    return {};
  }
}
export default class StatserWidget extends Component {
  view() {
    if (this.attrs.waitForDiscussions && !document.querySelector('.DiscussionList li')) {
      return m("div", null);
    }
    const forum = app.forum;
    if (!forum) return m("div", null);
    const canViewOnline = !!forum.attribute('statserCanViewOnline');
    const onlineUsers = canViewOnline ? forum.statserOnlineUsers?.() || [] : [];
    const totalOnline = canViewOnline ? Number(forum.attribute('statserTotalOnline')) || 0 : 0;
    const hiddenOnline = canViewOnline ? Number(forum.attribute('statserHiddenOnline')) || 0 : 0;
    const showGuests = canViewOnline && !!forum.attribute('statserShowOnlineGuests');
    const includeGuestsInTotal = showGuests && !!forum.attribute('statserIncludeGuestsInTotal');
    const onlineGuests = showGuests ? Number(forum.attribute('statserOnlineGuests')) || 0 : 0;
    const displayedOnline = includeGuestsInTotal ? totalOnline + onlineGuests : totalOnline;
    const showCurrentPage = canViewOnline && !!forum.attribute('statserShowCurrentPage');
    const activityMap = showCurrentPage ? parseActivityMap(forum.attribute('statserActivityMap')) : {};
    const discussionsCount = forum.attribute('statserDiscussionsCount');
    const postsCount = forum.attribute('statserPostsCount');
    const usersCount = forum.attribute('statserUsersCount');
    let latestUser = null;
    try {
      latestUser = forum.statserLatestUser?.();
    } catch (e) {}
    const hasOnlineRow = canViewOnline;
    const hasStatsRow = discussionsCount != null || postsCount != null || usersCount != null || !!latestUser;
    if (!hasOnlineRow && !hasStatsRow) {
      return m("div", null);
    }
    const maxAvatars = Math.max(1, Number(forum.attribute('statserMaxAvatars')) || 24);
    const visibleUsers = onlineUsers.slice(0, maxAvatars);
    const overflow = Math.max(0, totalOnline - onlineUsers.length - hiddenOnline);
    const extraOverflow = Math.max(0, onlineUsers.length - visibleUsers.length);
    const totalOverflow = overflow + extraOverflow;
    const renderUserAvatar = user => {
      const entry = activityMap[String(user.id())];
      const tooltipParts = [extractText(username(user))];
      if (showCurrentPage) {
        // `private` is a server-verified flag (see PresenceHeartbeatController) —
        // when set, we always render our own translated placeholder and ignore
        // whatever `label` the client happened to send, even if it looks safe.
        const page = entry?.private ? extractText(app.translator.trans('forumaker-statser.forum.routes.private_discussion')) : entry?.label ?? null;
        const isStandalone = !entry?.private && !!entry?.standalone;
        tooltipParts.push(page != null ? isStandalone ? page : extractText(app.translator.trans(PRE + 'viewing_page', {
          page
        })) : extractText(app.translator.trans(PRE + 'viewing_unknown')));
      }
      return m(Tooltip, {
        text: tooltipParts.join(' · '),
        key: user.id()
      }, m(Link, {
        href: app.route('user', {
          username: user.slug()
        }),
        className: "StatserWidget-avatar"
      }, m("span", {
        "aria-hidden": "true"
      }, m(Avatar, {
        user: user
      }))));
    };
    const onlineRow = hasOnlineRow ? m("div", {
      className: "StatserWidget-onlineRow"
    }, m("div", {
      className: "StatserWidget-onlineHeader"
    }, m("span", {
      className: "StatserWidget-onlineDot",
      "aria-hidden": "true"
    }), m("span", {
      className: "StatserWidget-onlineTitle"
    }, app.translator.trans(PRE + 'online_label')), displayedOnline > 0 && m("span", {
      className: "StatserWidget-onlineCount"
    }, formatNumber(displayedOnline))), visibleUsers.length > 0 || totalOverflow > 0 || hiddenOnline > 0 || onlineGuests > 0 ? m("div", {
      className: "StatserWidget-avatars"
    }, visibleUsers.map(renderUserAvatar), totalOverflow > 0 ? m(Tooltip, {
      text: extractText(app.translator.trans(PRE + 'online_users_count', {
        count: totalOverflow
      }))
    }, m("span", {
      className: "StatserWidget-badge StatserWidget-badge--overflow"
    }, "+", formatNumber(totalOverflow))) : null, hiddenOnline > 0 ? m(Tooltip, {
      text: extractText(app.translator.trans(PRE + 'hidden_users_count', {
        count: hiddenOnline
      }))
    }, m("span", {
      className: "StatserWidget-badge StatserWidget-badge--hidden"
    }, formatNumber(hiddenOnline))) : null, onlineGuests > 0 ? m(Tooltip, {
      text: extractText(app.translator.trans(PRE + 'online_guests_count', {
        count: onlineGuests
      }))
    }, m("span", {
      className: "StatserWidget-badge StatserWidget-badge--guest"
    }, formatNumber(onlineGuests))) : null) : m("span", {
      className: "StatserWidget-empty"
    }, app.translator.trans(PRE + 'empty_online'))) : null;
    const statChips = [];
    if (usersCount != null) {
      statChips.push(m("div", {
        className: "StatserWidget-stat",
        key: "users"
      }, m("i", {
        className: "StatserWidget-statIcon icon fas fa-users",
        "aria-hidden": "true"
      }), m("span", {
        className: "StatserWidget-statValue"
      }, formatNumber(usersCount)), m("span", {
        className: "StatserWidget-statLabel"
      }, app.translator.trans(PRE + 'total_users', {
        count: usersCount
      }))));
    }
    if (discussionsCount != null) {
      statChips.push(m("div", {
        className: "StatserWidget-stat",
        key: "discussions"
      }, m("i", {
        className: "StatserWidget-statIcon icon fas fa-comments",
        "aria-hidden": "true"
      }), m("span", {
        className: "StatserWidget-statValue"
      }, formatNumber(discussionsCount)), m("span", {
        className: "StatserWidget-statLabel"
      }, app.translator.trans(PRE + 'discussions', {
        count: discussionsCount
      }))));
    }
    if (postsCount != null) {
      statChips.push(m("div", {
        className: "StatserWidget-stat",
        key: "posts"
      }, m("i", {
        className: "StatserWidget-statIcon icon fas fa-comment-dots",
        "aria-hidden": "true"
      }), m("span", {
        className: "StatserWidget-statValue"
      }, formatNumber(postsCount)), m("span", {
        className: "StatserWidget-statLabel"
      }, app.translator.trans(PRE + 'posts', {
        count: postsCount
      }))));
    }
    if (latestUser) {
      statChips.push(m("div", {
        className: "StatserWidget-stat StatserWidget-stat--latest",
        key: "latest"
      }, m("i", {
        className: "StatserWidget-statIcon icon fas fa-user-plus",
        "aria-hidden": "true"
      }), m(Link, {
        href: app.route('user', {
          username: latestUser.slug()
        }),
        className: "StatserWidget-latestUser",
        "aria-label": extractText(username(latestUser))
      }, m("span", {
        "aria-hidden": "true"
      }, m(Avatar, {
        user: latestUser
      })), m("span", {
        className: "StatserWidget-latestUsername",
        "aria-hidden": "true"
      }, username(latestUser)))));
    }
    const statsRow = hasStatsRow && statChips.length > 0 ? m("div", {
      className: "StatserWidget-statsRow"
    }, statChips) : null;
    const hasBothRows = hasOnlineRow && statsRow;
    return m("section", {
      className: "StatserWidget",
      "aria-label": extractText(app.translator.trans(PRE + 'title'))
    }, m("div", {
      className: 'StatserWidget-panel' + (hasBothRows ? ' StatserWidget-panel--divided' : '')
    }, onlineRow, statsRow));
  }
}
flarum.reg.add('forumaker-statser', 'forum/components/StatserWidget', StatserWidget);